import React, { useState } from 'react';
import AdaptiveEventImage from 'src/components/molecules/AdaptiveEventImage';
import StaticMap from 'src/components/molecules/Map/Static';
import EventDetailButton from 'src/components/molecules/EventDetailButton';
import DateStamp from 'src/components/atoms/DateStamp';
import Button from 'src/components/atoms/Button';
import EventFullDetailsList from 'src/components/molecules/EventFullDetailsList';
import EventTags from 'src/components/molecules/EventTags';
import Tag from 'src/components/atoms/Tag';
import Social from './Social';
import ContactDetails from './ContactDetails';
import EditorialContent from './EditorialContent';
import { usePluginConfig } from 'src/contexts/pluginContext';
import Link from 'src/components/atoms/Link';
import EventDetailsAd from "../../../molecules/EventDetailsAd"

const Carousel = React.lazy(() => import('./Carousel'));
const EventDetail = ({ event, editorialContent, signedStaticMapUrl }) => {
  const pluginConfig = usePluginConfig();
  const publisher = pluginConfig.publisherSettings.publisher;
  const [showCarousel, setShowCarousel] = useState(false);

  const isFeatured = event.isFeatured(publisher.id);
  const canShowCarousel = isFeatured && event.imageUrls?.length && event.imageUrls.length > 1;

  return (
    <div className="grid @md:grid-cols-10 supports-not-cq:md:grid-cols-10 gap-4 w-full">
      <div className="@md:col-span-6 supports-not-cq:md:col-span-6 relative h-min">
        <div className="absolute top-5 z-10">
          <DateStamp event={event} />
        </div>

        {canShowCarousel && !showCarousel && (
          <div className="absolute bottom-2 right-2 z-10">
            <Button
              text="More images"
              cancel
              extraClasses="text-xs font-light hover:shadow"
              onClick={() => setShowCarousel(true)}
            />
          </div>
        )}

        {event.fullImageUrl && !showCarousel && (
          <AdaptiveEventImage
            src={event.fullImageUrl}
            loading="eager"
            alt={event.title}
          />
        )}
        {showCarousel && (
          <div className="relative image-placeholder">
            <Carousel imageUrls={event.imageUrls} title={event.title} />
          </div>
        )}
      </div>
      <div className="@md:col-span-4 supports-not-cq:md:col-span-4 px-2 flex flex-col h-full">
        <div className="flex-grow">
          <h1 className="font-bold text-3xl @md:text-xl @lg:text-3xl">{event.title}</h1>

          <EventFullDetailsList event={event} />
        </div>
      </div>
      <div className="@md:col-span-10 supports-not-cq:md:col-span-10 @md:border-t @md:border-b @md:p-2">
        <div className="grid @md:grid-cols-10">
          <div className="@md:col-span-6">
            <Social event={event} extraClasses="@md:text-xl" />
          </div>
          <div className="@md:col-span-4 @md:ml-5">
            {event.mainLink && !event.isExpired && <EventDetailButton event={event} />}
          </div>
        </div>
      </div>
      <div className="@md:col-span-6 supports-not-cq:md:col-span-6 text-base leading-5 px-2 mt-2">
        <EditorialContent content={editorialContent?.content} headline={editorialContent?.headline} />
        {!!event.summary && <div className="font-semibold mb-2">{event.summary}</div>}

        {event.videoUrl && (
          <div className="my-8">
            <iframe title="Video" className="w-full h-72" src={event.videoUrl}></iframe>
          </div>
        )}

        <div
          className="mt-6 mb-2 whitespace-pre-wrap text-gray-700"
          style={{ wordBreak: 'break-word' }}
        >
          {event.fullDescriptionWithoutSummary}
        </div>

        <div className="my-8 leading-9">
          <EventTags event={event} />
          <Link href={{ pathname: '/', query: { towns: event.town } }} className="mr-1">
            <Tag text={event.town} />
          </Link>
          {event.virtual && <Tag text="Virtual" />}
          {event.hashtag && <Tag text={event.hashtag} />}
        </div>
      </div>
      <div className="@md:col-span-4 supports-not-cq:md:col-span-4 px-5">
        {!event.virtual && event.latitude && event.longitude && signedStaticMapUrl && (
          <div className="pt-2 pb-6">
            <StaticMap event={event} signedStaticMapUrl={signedStaticMapUrl} />
          </div>
        )}

        {event.contact && <ContactDetails event={event} />}
        <EventDetailsAd/>
      </div>
    </div>
  );
};

export default EventDetail;
